import {
  SelectionGroup,
  SelectionItem,
} from "../../../Components/Selection/Selection";
import { LightIcon } from "../../../AssetComponents/Images";
import { RentalItem } from "../../../Components/RealEstateProperty/RentalItem";
import { useSelector, useDispatch } from "react-redux";
import { PropertyImageWithFallback } from "../../../Components/Images/PropertyImageWithFallback";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Fade } from "react-awesome-reveal";
import NoTripsyet from "../../../Components/DashBoardSvg/NoTripsyet";
import { getMyTrips } from "../../../Components/functions/Functions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../Components/functions/Api";
import { updateNFTStore } from "../../../ReduxSlices/NFTSlice";

const STATUS_MAP = ["upcoming", "current", "completed", "cancelled"];
const PAGE_SIZE = 20;
const TRIP_UPDATE_EVENTS = [
  "reservationStatusUpdate",
  "reservation:confirmed",
  "reservation:updated",
  "reservation:approved",
  "reservation:cancelled",
  "reservation:rejected",
  "reservation:finalized",
  "reservation:pending.v2",
  "reservationConfirmed",
  "reservationUpdated",
  "reservationApproved",
  "reservationCancelled",
  "reservationFinalized",
  "reservationRejected",
  "reservationCreated",
];

export const Trips = () => {
  const dispatch = useDispatch();
  const account = useSelector((state) => state.auth.account);
  const diffToUTC = useSelector((state) => state.time.diffToUTC);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [flag, setFlag] = useState(0);
  const [page, setPage] = useState(1);

  const normalizedAccount = useMemo(
    () => (account ? account.toLowerCase().trim() : null),
    [account]
  );

  const activeStatus = useMemo(
    () => STATUS_MAP[flag] || "all",
    [flag]
  );

  const baseQueryKey = useMemo(
    () => ["travelerTrips", normalizedAccount],
    [normalizedAccount]
  );

  const tripsQuery = useQuery({
    queryKey: [...baseQueryKey, activeStatus, page, diffToUTC],
    queryFn: () =>
      getMyTrips({
        account: normalizedAccount,
        status: activeStatus,
        diffToUTC,
        page,
        limit: PAGE_SIZE,
        dispatch,
      }),
    enabled: Boolean(normalizedAccount),
    keepPreviousData: false, // Don't show previous tab's list when switching (avoids wrong count + extra listings)
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const { data, isLoading, isFetching } = tripsQuery;

  const rawTrips = data?.trips || [];
  const trips = useMemo(() => {
    const seen = new Set();
    return rawTrips.filter((t) => {
      const period = t.reservation?.renting_period;
      const id =
        period && period.length >= 2
          ? `${t.nft?.token_id}-${t.reservation?.address}-${period[0]}-${period[1]}`
          : `${t.nft?.token_id}-${t.reservation?.address}-${t.key ?? Math.random()}`;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [rawTrips]);
  const totalCount = trips.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasMore = data?.hasMore ?? page < totalPages;

  const setPeriodParams = useCallback((token_id, from, to, cancelled = false) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("from", from);
    searchParams.set("to", to);
    navigate(location.pathname + `/${token_id}?${searchParams.toString()}`, {
      state: cancelled ? { cancelled: true } : undefined,
    });
  }, [location.search, location.pathname, navigate]);

  const handleFlagChange = useCallback((value) => {
    setFlag(value);
    setPage(1);
  }, []);

  const handleNextPage = useCallback(() => {
    setPage((prev) => Math.min(prev + 1, totalPages));
  }, [totalPages]);

  const handlePrevPage = useCallback(() => {
    setPage((prev) => Math.max(prev - 1, 1));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [normalizedAccount]);

  // 🚀 INSTANT DASHBOARDS: Listen for socket-based reservation updates and invalidate query cache
  // 🚀 PHASE 6: Instant updates - no debounce for reservation status changes
  useEffect(() => {
    if (!normalizedAccount) return;
    
    const invalidateTrips = async (event) => {
      const detail = event?.detail || {};
      console.log("🔄 Trip update detected, invalidating traveler trips cache...", detail);
      
      // 🚀 PHASE 6: Refresh property data in Redux first if token_id is provided
      if (detail?.token_id && dispatch) {
        try {
          // Refresh property in background to ensure latest reservation status
          const propertyData = await api("property/getProperty", { token_id: detail.token_id });
          if (propertyData) {
            dispatch(
              updateNFTStore({
                token_id: detail.token_id,
                metaData: propertyData.metaData,
                short: propertyData.short,
                rentals: propertyData.rentals,
                access: propertyData.access,
              })
            );
            console.log("✅ Property refreshed in Redux for trips update");
          }
        } catch (error) {
          console.warn("Failed to refresh property for trips update:", error);
        }
      }
      
      // 🚀 PHASE 6: Immediately invalidate and refetch (no debounce for instant updates)
      // Use refetchType: 'active' to immediately refetch active queries
      queryClient.invalidateQueries({ 
        queryKey: baseQueryKey,
        refetchType: 'active' // Immediately refetch active queries
      });
    };

    TRIP_UPDATE_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, invalidateTrips)
    );

    return () => {
      TRIP_UPDATE_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, invalidateTrips)
      );
    };
  }, [baseQueryKey, normalizedAccount, queryClient, dispatch]);

  return (
    <div className="w-full overflow-auto max-h-[calc(100vh-80px)] h-full p-[8px] sm:p-[10px]">
      <div className="w-full h-full space-y-[16px] sm:space-y-[20px] rounded-[10px] p-[4px] sm:p-[8px] flex flex-col">
        <div className="h-max p-[16px] sm:p-[24px] rounded-[10px]">
          <div className="flex items-center gap-[8px] sm:gap-[10px]">
            <div className="p-[6px] sm:p-[8px] w-[28px] h-[28px] sm:w-[32px] sm:h-[32px] flex items-center justify-center rounded-full bg-[#5b1dee] scale-75 sm:scale-100">
              <svg
                width="17"
                height="14"
                viewBox="0 0 17 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g filter="url(#filter0_d_672_35088)">
                  <path
                    d="M13.2492 11.8125C13.2492 11.9285 13.2031 12.0398 13.1211 12.1219C13.039 12.2039 12.9277 12.25 12.8117 12.25H4.93671C4.82068 12.25 4.7094 12.2039 4.62735 12.1219C4.5453 12.0398 4.49921 11.9285 4.49921 11.8125C4.49921 11.6965 4.5453 11.5852 4.62735 11.5031C4.7094 11.4211 4.82068 11.375 4.93671 11.375H12.8117C12.9277 11.375 13.039 11.4211 13.1211 11.5031C13.2031 11.5852 13.2492 11.6965 13.2492 11.8125ZM12.3742 5.25H9.05577L6.55874 2.75297C6.51808 2.71235 6.46981 2.68015 6.4167 2.65819C6.36359 2.63623 6.30668 2.62495 6.24921 2.625H5.81171C5.6731 2.62508 5.5365 2.65808 5.41314 2.72129C5.28979 2.7845 5.18322 2.87612 5.10221 2.98858C5.02119 3.10105 4.96806 3.23115 4.94718 3.36818C4.92629 3.5052 4.93826 3.64523 4.9821 3.77672L5.47429 5.25H4.24327L3.05874 4.06547C3.01808 4.02485 2.96981 3.99265 2.9167 3.97069C2.86359 3.94873 2.80668 3.93745 2.74921 3.9375H2.31171C2.17536 3.93762 2.04092 3.96961 1.91913 4.03091C1.79733 4.09221 1.69155 4.18112 1.61022 4.29056C1.52888 4.4 1.47425 4.52693 1.45068 4.66122C1.4271 4.79552 1.43524 4.93347 1.47444 5.06406L2.2439 7.62836C2.37707 8.08013 2.65337 8.47642 3.03121 8.75761C3.40905 9.03879 3.86799 9.18966 4.27071 9.60584C4.76612 9.81105 5.2971 9.91667 5.83333 9.91667C6.36956 9.91667 6.90054 9.81105 7.39596 9.60584C7.89137 9.40063 8.34151 9.09986 8.72069 8.72069C9.09986 8.34151 9.40063 7.89137 9.60584 7.39596C9.81105 6.90054 9.91667 6.36956 9.91667 5.83333C9.91667 5.2971 9.81105 4.76612 9.60584 4.27071C9.40063 3.7753 9.09986 3.32515 8.72069 2.94598C8.34151 2.56681 7.89137 2.26603 7.39596 2.06083C6.90054 1.85562 6.36956 1.75 5.83333 1.75C5.2971 1.75 4.76612 1.85562 4.27071 2.06083C3.7753 2.26603 3.32515 2.56681 2.94598 2.94598C2.56681 3.32515 2.26603 3.7753 2.06083 4.27071C1.85562 4.76612 1.75 5.2971 1.75 5.83333Z"
                    fill="white"
                  />
                  <path
                    d="M4.9821 3.77672C4.93826 3.64523 4.92629 3.5052 4.94718 3.36818C4.96806 3.23115 5.02119 3.10105 5.10221 2.98858M4.9821 3.77672L5.81171 2.625C5.6731 2.62508 5.5365 2.65808 5.41314 2.72129C5.28979 2.7845 5.18322 2.87612 5.10221 2.98858M4.9821 3.77672L5.39077 5L4.9821 3.77672ZM5.10221 2.98858L4.89935 2.84246C4.79519 2.98706 4.72688 3.15434 4.70003 3.33051C4.67318 3.50669 4.68857 3.68672 4.74493 3.85578L4.74498 3.85593L5.12719 5M5.10221 2.98858L4.89935 2.84246C5.00351 2.69786 5.14053 2.58008 5.29913 2.4988C5.45773 2.41753 5.63336 2.3751 5.81157 2.375L5.81171 2.375L6.24901 2.375L5.12719 5M5.12719 5H4.34682L3.23552 3.88869L3.23542 3.88859C3.17152 3.82477 3.09568 3.77416 3.01222 3.73966C2.92882 3.70518 2.83945 3.68745 2.74921 3.6875C2.7492 3.6875 2.7492 3.6875 2.74919 3.6875C2.74913 3.6875 2.74907 3.6875 2.74901 3.6875L2.31171 3.6875L2.31148 3.6875C2.13617 3.68766 1.96333 3.72879 1.80674 3.8076C1.65014 3.88641 1.51413 4.00073 1.40956 4.14143C1.30499 4.28214 1.23475 4.44533 1.20444 4.618C1.17413 4.79066 1.18459 4.96801 1.23499 5.13591C1.23499 5.13592 1.23499 5.13593 1.235 5.13594L2.0041 7.69905C2.00416 7.69925 2.00422 7.69946 2.00428 7.69967C2.15284 8.20313 2.46083 8.64477 2.88195 8.95816C3.30308 9.27156 3.81455 9.43977 4.33948 9.4375M5.12719 5L6.24921 2.375C6.33945 2.37495 6.42882 2.39268 6.51222 2.42716C6.59568 2.46166 6.67152 2.51227 6.73542 2.57609L6.73552 2.57619L9.15932 5H12.3742C13.0207 5 13.6407 5.25681 14.0978 5.71393C14.5549 6.17105 14.8117 6.79103 14.8117 7.4375V8.75C14.8117 8.93234 14.7393 9.1072 14.6103 9.23614C14.4814 9.36507 14.3065 9.4375 14.1242 9.4375H4.33948M4.33948 9.4375H4.33897V9.1875L4.34012 9.4375C4.3399 9.4375 4.33969 9.4375 4.33948 9.4375ZM13.2978 12.2986C13.4268 12.1697 13.4992 11.9948 13.4992 11.8125C13.4992 11.6302 13.4268 11.4553 13.2978 11.3264C13.1689 11.1974 12.994 11.125 12.8117 11.125H4.93671C4.75437 11.125 4.5795 11.1974 4.45057 11.3264C4.32164 11.4553 4.24921 11.6302 4.24921 11.8125C4.24921 11.9948 4.32164 12.1697 4.45057 12.2986C4.5795 12.4276 4.75437 12.5 4.93671 12.5H12.8117C12.994 12.5 13.1689 12.4276 13.2978 12.2986Z"
                    stroke="#202020"
                    strokeWidth="0.5"
                  />
                </g>
                <defs>
                  <filter
                    id="filter0_d_672_35088"
                    x="0.9375"
                    y="2.125"
                    width="15.125"
                    height="11.625"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dx="1" dy="1" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0"
                    />
                    <feBlend
                      mode="normal"
                      in2="BackgroundImageFix"
                      result="effect1_dropShadow_672_35088"
                    />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="effect1_dropShadow_672_35088"
                      result="shape"
                    />
                  </filter>
                </defs>
              </svg>
            </div>

            <div className="font-semibold text-[20px] sm:text-[24px]">My Trips</div>
          </div>
        </div>

        <div className="w-full px-[12px] sm:px-[20px] justify-between flex flex-col sm:flex-row items-start sm:items-center gap-[12px] sm:gap-0">
          <SelectionGroup
            defaultItem={flag}
            className="border-[2px] border-[#e3e3e3] w-full sm:w-max px-[4px] sm:px-[6px] py-[3px] sm:py-[4px] gap-[4px] sm:gap-[8px] flex items-center rounded-[14px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
          >
            <SelectionItem
              SelectedItem={
                <div className="py-[3px] sm:py-[4px] cursor-pointer bg-white rounded-[10px] w-[80px] sm:w-[120px] flex justify-center gap-[6px] sm:gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <div className="text-black font-semibold text-xs sm:text-base whitespace-nowrap">Upcoming</div>
                  <div className="scale-50 sm:scale-100 flex-shrink-0">
                    <LightIcon />
                  </div>
                </div>
              }
              UnselectedItem={
                <div
                  onClick={() => handleFlagChange(0)}
                  className="py-[3px] sm:py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-[80px] sm:w-[120px] flex justify-center"
                >
                  <div className="text-[#959595] text-xs sm:text-base whitespace-nowrap">Upcoming</div>
                </div>
              }
            />
            <SelectionItem
              SelectedItem={
                <div className="py-[3px] sm:py-[4px] cursor-pointer bg-white rounded-[10px] w-[80px] sm:w-[120px] flex justify-center gap-[6px] sm:gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <div className="text-black font-semibold text-xs sm:text-base whitespace-nowrap">Current</div>
                  <div className="scale-50 sm:scale-100 flex-shrink-0">
                    <LightIcon />
                  </div>
                </div>
              }
              UnselectedItem={
                <div
                  onClick={() => handleFlagChange(1)}
                  className="py-[3px] sm:py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-[80px] sm:w-[120px] flex justify-center"
                >
                  <div className="text-[#959595] text-xs sm:text-base whitespace-nowrap">Current</div>
                </div>
              }
            />
            <SelectionItem
              SelectedItem={
                <div className="py-[3px] sm:py-[4px] cursor-pointer bg-white rounded-[10px] w-[80px] sm:w-[120px] flex justify-center gap-[6px] sm:gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <div className="text-black font-semibold text-xs sm:text-base whitespace-nowrap">Completed</div>
                  <div className="scale-50 sm:scale-100 flex-shrink-0">
                    <LightIcon />
                  </div>
                </div>
              }
              UnselectedItem={
                <div
                  onClick={() => handleFlagChange(2)}
                  className="py-[3px] sm:py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-[80px] sm:w-[120px] flex justify-center"
                >
                  <div className="text-[#959595] text-xs sm:text-base whitespace-nowrap">Completed</div>
                </div>
              }
            />
            <SelectionItem
              SelectedItem={
                <div className="py-[3px] sm:py-[4px] cursor-pointer bg-white rounded-[10px] w-[80px] sm:w-[120px] flex justify-center gap-[6px] sm:gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <div className="text-black font-semibold text-xs sm:text-base whitespace-nowrap">Cancelled</div>
                  <div className="scale-50 sm:scale-100 flex-shrink-0">
                    <LightIcon />
                  </div>
                </div>
              }
              UnselectedItem={
                <div
                  onClick={() => handleFlagChange(3)}
                  className="py-[3px] sm:py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-[80px] sm:w-[120px] flex justify-center"
                >
                  <div className="text-[#959595] text-xs sm:text-base whitespace-nowrap">Cancelled</div>
                </div>
              }
            />
          </SelectionGroup>

          <div className="flex gap-[8px] sm:gap-[10px] items-center border-[#E3E3E3] border-[1px] rounded-[12px] pl-[12px] sm:pl-[16px] pr-[6px] sm:pr-[8px] py-[8px] sm:py-[9px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] w-full sm:w-auto">
            <svg
              className="scale-75 sm:scale-100"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.25 12.25L8.75 8.75M1.75 5.83333C1.75 6.36956 1.85562 6.90054 2.06083 7.39596C2.26603 7.89137 2.56681 8.34151 2.94598 8.72069C3.32515 9.09986 3.7753 9.40063 4.27071 9.60584C4.76612 9.81105 5.2971 9.91667 5.83333 9.91667C6.36956 9.91667 6.90054 9.81105 7.39596 9.60584C7.89137 9.40063 8.34151 9.09986 8.72069 8.72069C9.09986 8.34151 9.40063 7.89137 9.60584 7.39596C9.81105 6.90054 9.91667 6.36956 9.91667 5.83333C9.91667 5.2971 9.81105 4.76612 9.60584 4.27071C9.40063 3.7753 9.09986 3.32515 8.72069 2.94598C8.34151 2.56681 7.89137 2.26603 7.39596 2.06083C6.90054 1.85562 6.36956 1.75 5.83333 1.75C5.2971 1.75 4.76612 1.85562 4.27071 2.06083C3.7753 2.26603 3.32515 2.56681 2.94598 2.94598C2.56681 3.32515 2.26603 3.7753 2.06083 4.27071C1.85562 4.76612 1.75 5.2971 1.75 5.83333Z"
                stroke="#202020"
                strokeWidth="1.16667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <input 
              placeholder="Search" 
              className="w-full outline-none text-sm sm:text-base" 
            />
            <svg
              className="scale-75 sm:scale-100"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="20" height="20" rx="4" fill="#E3E3E3" />
              <path
                d="M11.4837 6.38636L9.13991 15.0938H8.19176L10.5355 6.38636H11.4837Z"
                fill="#202020"
              />
            </svg>
          </div>
        </div>

        <div className="flex px-[16px] sm:px-[40px] gap-3 flex-wrap max-h-[calc(100vh-200px)] overflow-auto scrollbarwidth">
          {/* 🚀 INSTANT DASHBOARDS: Use backend data only (no fallback client-side filtering) */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center w-full h-[calc(100vh-300px)] px-4">
              <div className="font-semibold text-sm sm:text-base text-center mt-4">Loading trips...</div>
            </div>
          ) : trips.length > 0 ? (
            trips.map(({ nft, reservation, key }, index) => {
              const period = reservation?.renting_period;
              const uniqueKey = key && Array.isArray(period) && period.length >= 2
                ? `${key}-${period[0]}-${period[1]}`
                : `${key ?? nft?.token_id}-${index}`;
              const isCancelled = !!reservation?.cancelled;
              return (
              <Fade key={uniqueKey}>
                <RentalItem
                  traveler={reservation.address}
                  rentingPeriod={reservation.renting_period}
                  token_id={nft.token_id}
                  cancelled={isCancelled}
                  onClick={() =>
                    setPeriodParams(
                      nft.token_id,
                      reservation.renting_period[0],
                      reservation.renting_period[1],
                      isCancelled
                    )
                  }
                />
              </Fade>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center w-full h-[calc(100vh-300px)] px-4">
              <div className="scale-75 sm:scale-100">
                <NoTripsyet />
              </div>
              <div className="font-semibold text-sm sm:text-base text-center mt-4">No Trips yet</div>
              <div className="text-[#737373] text-xs sm:text-sm text-center mt-2">
                {flag === 0 && "No upcoming trips yet, explore your trips"}
                {flag === 1 && "No current trips"}
                {flag === 2 && "No completed trips"}
                {flag === 3 && "No cancelled trips"}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-between px-[16px] sm:px-[40px] gap-3">
          <div className="text-xs sm:text-sm text-[#737373]">
            Page {page} of {totalPages} • {totalCount} trip{totalCount === 1 ? "" : "s"} found
            {isFetching && !isLoading && <span className="ml-2 text-[#5b1dee]">Refreshing…</span>}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrevPage}
              disabled={page === 1 || isLoading}
              className="px-3 py-2 rounded-[8px] bg-white border border-[#e3e3e3] disabled:opacity-50 disabled:cursor-not-allowed shadow-[2px_2px_6px_0px_rgba(187,195,206,0.4)] text-sm font-semibold"
            >
              Prev
            </button>
            <button
              onClick={handleNextPage}
              disabled={!hasMore || isLoading}
              className="px-3 py-2 rounded-[8px] bg-[#5b1dee] text-white border border-[#5b1dee] disabled:opacity-50 disabled:cursor-not-allowed shadow-[2px_2px_6px_0px_rgba(91,29,238,0.4)] text-sm font-semibold"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
